import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { changePasswordSchema } from '@app/shared';
import type { ChangePasswordInput } from '@app/shared';
import { FormDialog } from '../../components/dialogs';
import { TextInput, applyServerErrors } from '../../components/form';
import { useNotify } from '../../components/NotifyProvider';
import { api } from '../../lib/api';

/** "Reset Password" in the user menu: the signed-in user changes their own password. */
export function ResetPasswordDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const change = useMutation({
    mutationFn: (input: ChangePasswordInput) => api.post('/auth/password', input),
  });
  const { control, handleSubmit, setError } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const submit = handleSubmit(async (values) => {
    try {
      await change.mutateAsync(values);
      notify.success('Your password was changed.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const field = (name: keyof ChangePasswordInput, label: string) => (
    <TextInput
      control={control}
      name={name}
      label={label}
      type="password"
      required
      autoComplete={name === 'currentPassword' ? 'current-password' : 'new-password'}
      slotProps={{ htmlInput: { maxLength: 200 } }}
    />
  );
  return (
    <FormDialog
      open
      title="Reset Password"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={change.isPending}
      submitLabel="Update"
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        {field('currentPassword', 'Current Password')}
        {field('newPassword', 'New Password')}
        {field('confirmPassword', 'Confirm Password')}
      </Box>
    </FormDialog>
  );
}
